/**
 * Correlation ID Management
 * 
 * Server generates correlation ID after successful login and sends to client.
 * Client stores in sessionStorage and includes in all API requests.
 * This enables tracking user journeys across client and server logs.
 */

const CORRELATION_ID_KEY = '__correlationId';
const CORRELATION_ID_LENGTH = 8;
const DEFAULT_CORRELATION_ID = 'NOCORR';

/**
 * Generate a random correlation ID (fallback for when server doesn't provide one)
 */
function generateCorrelationId(): string {
    return Math.random().toString(36).substring(2, 2 + CORRELATION_ID_LENGTH).toUpperCase();
}

/**
 * Get the current correlation ID
 * - Returns stored ID from sessionStorage if available
 * - Returns default ID if not in browser context or no ID set
 */
export function getCorrelationId(): string {
    if (typeof window === 'undefined') {
        // Server-side: should use server correlation ID from AsyncLocalStorage
        return 'SERVER';
    }

    try {
        const correlationId = sessionStorage.getItem(CORRELATION_ID_KEY);
        return correlationId || DEFAULT_CORRELATION_ID;
    } catch (e) {
        // SessionStorage not available (private browsing, etc.)
        return DEFAULT_CORRELATION_ID;
    }
}

/**
 * Set correlation ID (typically called after receiving from server on login)
 * 
 * @param id - Correlation ID from server
 */
export function setCorrelationId(id: string) {
    if (typeof window !== 'undefined') {
        try {
            sessionStorage.setItem(CORRELATION_ID_KEY, id);
        } catch (e) {
            // SessionStorage not available
            console.warn('Failed to store correlation ID:', e);
        }
    }
}

/**
 * Clear correlation ID (on logout)
 */
export function clearCorrelationId() {
    if (typeof window !== 'undefined') {
        try {
            sessionStorage.removeItem(CORRELATION_ID_KEY);
        } catch (e) {
            // SessionStorage not available
        }
    }
}

/**
 * Initialize correlation ID if not already set
 * Called on app startup to ensure we always have an ID
 */
export function initializeCorrelationId() {
    if (typeof window !== 'undefined') {
        const existing = getCorrelationId();
        const serverInjectedId = (window as any).__SERVER_CORRELATION_ID__;

        // Any existing ID that is not default and not temporary is considered "valid" (e.g. USR- ID)
        const hasValidExistingId = existing !== DEFAULT_CORRELATION_ID && !existing.startsWith('TEMP-');

        // If we have a server ID and no valid existing ID, adopt the server ID
        // This connects the initial page load (server) with the client session
        if (serverInjectedId && !hasValidExistingId) {
            setCorrelationId(serverInjectedId);
            return serverInjectedId;
        }

        if (existing === DEFAULT_CORRELATION_ID) {
            // Generate temporary ID until server provides real one
            const tempId = `TEMP-${generateCorrelationId()}`;
            setCorrelationId(tempId);
            return tempId;
        }
        return existing;
    }
    return DEFAULT_CORRELATION_ID;
}

/**
 * Check if current correlation ID is temporary (not from server)
 */
export function hasServerCorrelationId(): boolean {
    const id = getCorrelationId();
    return id !== DEFAULT_CORRELATION_ID && !id.startsWith('TEMP-');
}
